// ── Manual Note Order ──────────────────────────────────────────────────────
// Pure functions behind the "Manual" sort mode. Mirrored 1:1 in Swift
// (SortOrder.swift) — change both or neither.
//
// Model: each note carries an optional `sortOrder` (epoch-ms-scale Double).
// A null/undefined sortOrder falls back to the note's createdAt epoch, so an
// un-reordered manual list is just created-newest-first and brand-new notes
// land at the top for free. Sorted DESC (higher value = higher in the list).
// Only an explicit drag ever writes a value.

import type { Note } from "@caliu/types"

/** Spacing used when dropping at the very top/bottom of the list (1 min in ms scale). */
export const MANUAL_ORDER_GAP = 60000

/**
 * Epoch milliseconds for a `created_at` string, interpreted as **UTC**.
 *
 * `created_at` is canonically UTC, but the format varies: web/iOS write ISO
 * with a `Z` (`2026-06-16T22:00:00.000Z`) while the server's `datetime('now')`
 * writes a naive `2026-06-16 22:00:00` (no zone). `Date.parse` treats a naive
 * string as **local time**, which diverges from the SQLite `julianday` fallback
 * used to order the list (julianday treats naive strings as UTC). That mismatch
 * put a dropped note's computed `sort_order` hours off the list's own scale, so
 * reorders of server-created notes silently landed in the wrong place. Normalize
 * naive strings to UTC so the JS order key and the SQL order key share one scale.
 */
export function createdAtToEpochMs(createdAt: string): number {
  let s = createdAt
  const hasZone = /[zZ]$|[+-]\d\d:?\d\d$/.test(s)
  if (!hasZone) s = `${s.replace(" ", "T")}Z`
  const ms = Date.parse(s)
  return Number.isNaN(ms) ? 0 : ms
}

/**
 * The order key a note sorts by in Manual mode: its explicit `sortOrder`, or
 * its createdAt epoch (UTC) as a fallback. Sorted DESC by callers.
 */
export function noteEffectiveOrder(note: Pick<Note, "sortOrder" | "createdAt">): number {
  if (note.sortOrder != null) return note.sortOrder
  return createdAtToEpochMs(note.createdAt)
}

/**
 * Compute a new sortOrder for a note dropped between two neighbors, given the
 * effective order of the note now ABOVE it and the note now BELOW it (list is
 * DESC, so above > below). Either may be undefined when dropping at an edge.
 *
 *  - both present → midpoint
 *  - only `below` (dropped at the very top) → below + GAP
 *  - only `above` (dropped at the very bottom) → above - GAP
 *  - neither (empty list) → 0
 *  - equal neighbors (precision exhausted) → above + GAP, so the value still
 *    sorts strictly above `below`; the caller's next reorder renormalizes.
 */
export function manualOrderBetween(above: number | undefined, below: number | undefined): number {
  if (above != null && below != null) {
    if (above <= below) return below + MANUAL_ORDER_GAP
    return below + (above - below) / 2
  }
  if (below != null) return below + MANUAL_ORDER_GAP
  if (above != null) return above - MANUAL_ORDER_GAP
  return 0
}
